import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import styles from './Interactive.module.css';

interface FillBlankConfig {
  textWithBlanks: string;
  acceptedAnswers: string[];
}

interface FillBlankProps {
  config: FillBlankConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
  isCorrect?: boolean;
}

export function FillBlank({ config, onSubmit, disabled, showResult, isCorrect }: FillBlankProps) {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (disabled || !value.trim()) return;
    onSubmit(value.trim());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const getInputClass = () => {
    let className = styles.blankInput;
    if (showResult) {
      className += isCorrect ? ` ${styles.correct}` : ` ${styles.incorrect}`;
    }
    return className;
  };

  // Parse text and replace blank markers with input
  const renderText = () => {
    const text = config.textWithBlanks;
    // Match ___, {{blank}}, or similar patterns
    const parts = text.split(/(_{3,}|\{\{blank\}\}|\{\{.*?\}\})/g);

    return parts.map((part, idx) => {
      if (part.match(/^(_{3,}|\{\{blank\}\}|\{\{.*?\}\})$/)) {
        return (
          <input
            key={idx}
            type="text"
            className={getInputClass()}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="answer"
            disabled={disabled}
            autoFocus
          />
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <div className={styles.fillBlankContainer}>
      <div className={styles.fillBlankText}>{renderText()}</div>

      {!showResult && (
        <div className={styles.inputContainer}>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={disabled || !value.trim()}
          >
            Submit Answer
          </button>
        </div>
      )}
    </div>
  );
}
